import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';
import { HugeiconsIcon } from '@hugeicons/react';
import { Clock01IconStroke } from '@/components/icons';

import { T } from '../../util/Localize';
import { TextField } from '../../components/input';
import { useForm } from '../../hooks/use-form';
import { formatZodError, isValidPhone } from '../../util/validation';
import { useHost } from '../../hooks/use-host';
import { HUB_LOGIN_PATH } from '../../routes/paths';
import {
    REFERRAL_CODE_LENGTH,
    REGISTER_CLIENT_FIELDS,
    canRegisterOnBrand,
    isRegistrationUnavailable,
    registerClient,
    registrationFieldErrors
} from '../../services/registration';
import { HttpError } from '../../services/http';
import { AuthPageLayout } from './auth-page-layout';
import { authSubmitButtonSx } from './auth-submit-button';
import { GoogleSignInButton } from './google-sign-in-button';
import { SuspendedAccountNotice } from '../../components/errors/suspended-account-notice';
import type { SsoOutcome } from '../../services/google-sso';

const HEADING = 'Create your account.';
const BODY = 'Set up your account to view, download, and share your listing media.';

const schema = z.object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    companyName: z.string().trim().min(1, 'Brokerage/Company name is required'),
    email: z.email('Enter a valid email address'),
    phone: z
        .string()
        .trim()
        .min(1, 'Phone number is required')
        .refine(value => isValidPhone(value), 'Enter a valid phone number'),
    referralCode: z
        .string()
        .trim()
        .refine(
            value => value === '' || new RegExp(`^\\d{${REFERRAL_CODE_LENGTH}}$`).test(value),
            `Referral codes are ${REFERRAL_CODE_LENGTH} digits`
        )
        .optional()
});

type FormData = z.infer<typeof schema>;

const EMAIL_TAKEN_MESSAGE = 'An account already exists for this email address.';

/** Which inline Google message the form shows. */
type GoogleSignUpError = 'failed' | 'email-in-use' | 'suspended';

/**
 * UX-803: the inline message for a Google return that reached the form. `email-in-use` gets its
 * own message with a sign-in link, and `suspended` (ROBO-481: the address matched an account of a
 * suspended business that may not sign in) the Account Inactive notice; everything else is the
 * generic failure — including `created` / `success`, which only reach the form when the returned
 * session did not authorize, and `no-account`, which a sign-up should never produce.
 */
function googleSignUpErrorFor(ssoOutcome: SsoOutcome | undefined): GoogleSignUpError | null {
    if (!ssoOutcome) return null;
    if (ssoOutcome === 'email-in-use' || ssoOutcome === 'suspended') return ssoOutcome;
    return 'failed';
}

export interface RegisterProps {
    /** UX-803: the Google sign-up return outcome, when this page load is one (`hub-register-page.tsx`). */
    ssoOutcome?: SsoOutcome;
}

export default function Register({ ssoOutcome }: RegisterProps = {}) {
    const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
    const [refused, setRefused] = useState(false);
    const { data: hostInfo, isLoading, isError } = useHost();

    // Once the host is known, a brand that cannot take registrations never gets a usable form (API-507).
    const brandUnavailable = !isLoading && (isError || !canRegisterOnBrand(hostInfo?.brandId));
    const unavailable = refused || brandUnavailable;

    useEffect(() => {
        if (brandUnavailable) {
            console.warn('Registration unavailable for this host; brandId:', hostInfo?.brandId);
        }
    }, [brandUnavailable, hostInfo?.brandId]);

    return (
        <AuthPageLayout>
            {confirmedEmail ? (
                <RegistrationConfirmation email={confirmedEmail} />
            ) : unavailable ? (
                <RegistrationUnavailable />
            ) : (
                <RegistrationForm
                    onRegistered={setConfirmedEmail}
                    ssoOutcome={ssoOutcome}
                    onRefused={() => setRefused(true)}
                />
            )}
        </AuthPageLayout>
    );
}

function RegistrationForm({
    onRegistered,
    ssoOutcome,
    onRefused
}: {
    onRegistered: (email: string) => void;
    ssoOutcome?: SsoOutcome;
    onRefused: () => void;
}) {
    const { data: hostInfo } = useHost();
    const [submitting, setSubmitting] = useState(false);
    const [googleError, setGoogleError] = useState<GoogleSignUpError | null>(() =>
        googleSignUpErrorFor(ssoOutcome)
    );
    // UX-797 / API-507: undefined on a brand that cannot take a client registration (brand 1).
    const brandId = canRegisterOnBrand(hostInfo?.brandId) ? hostInfo?.brandId : undefined;

    const { values, errors, getFieldProps, handleSubmit, setFieldError, setFieldValue } = useForm<FormData>({
        initialValues: {
            firstName: '',
            lastName: '',
            companyName: '',
            email: '',
            phone: '',
            referralCode: ''
        },
        validationMode: 'onSubmit',
        validate: v => {
            const result = schema.safeParse(v);
            return result.success ? {} : formatZodError(result.error);
        }
    });

    const canSubmit =
        brandId !== undefined &&
        values.firstName.trim().length > 0 &&
        values.lastName.trim().length > 0 &&
        values.companyName.trim().length > 0 &&
        values.phone.trim().length > 0 &&
        z.email().safeParse(values.email).success;

    const onSubmit = () => {
        setGoogleError(null);
        return handleSubmit(async submitted => {
            if (brandId === undefined) {
                onRefused();
                return;
            }
            setSubmitting(true);
            try {
                const registeredEmail = await registerClient({
                    firstName: submitted.firstName,
                    lastName: submitted.lastName,
                    companyName: submitted.companyName,
                    email: submitted.email,
                    phone: submitted.phone,
                    referralCode: submitted.referralCode || undefined,
                    brandId
                });
                onRegistered(registeredEmail);
            } catch (error) {
                const fieldErrors = registrationFieldErrors(error);
                if (isRegistrationUnavailable(error)) {
                    console.warn('Registration refused for this brand; brandId:', brandId, error);
                    onRefused();
                } else if (error instanceof HttpError && error.status === 409) {
                    setFieldError('email', EMAIL_TAKEN_MESSAGE);
                } else if (Object.keys(fieldErrors).length > 0) {
                    // A 400 naming form fields (a brandId 400 was handled above).
                    for (const field of REGISTER_CLIENT_FIELDS) {
                        if (fieldErrors[field]) setFieldError(field, fieldErrors[field]);
                    }
                } else {
                    setFieldError('email', 'Something went wrong. Please try again.');
                }
            } finally {
                setSubmitting(false);
            }
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={4}>
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 900, fontSize: 36, lineHeight: 1 }}>
                    {T(HEADING)}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1 }}>
                    {T(BODY)}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={1.25}>
                <GoogleSignInButton
                    label={T('Sign up with Google')}
                    intent="signup"
                    disabled={submitting}
                    onRedirectStart={() => setGoogleError(null)}
                    onRedirectError={() => setGoogleError('failed')}
                />
                {googleError === 'failed' && (
                    <Typography sx={{ color: '#ff8a80', fontSize: 13 }}>
                        {T("We couldn't sign you up with Google. Please try again.")}
                    </Typography>
                )}
                {googleError === 'email-in-use' && (
                    <Typography sx={{ color: '#ff8a80', fontSize: 13 }}>
                        {T('An account with this email already exists.')}{' '}
                        <RouterLink
                            to={HUB_LOGIN_PATH}
                            style={{ color: 'inherit', fontWeight: 700 }}
                        >
                            {T('Sign in instead.')}
                        </RouterLink>
                    </Typography>
                )}
                {googleError === 'suspended' && <SuspendedAccountNotice sx={{ mt: 0.5 }} />}
                <Divider
                    sx={{
                        my: 0.5,
                        color: 'inherit',
                        fontSize: 13,
                        '&::before, &::after': { borderTop: '1px solid currentColor', opacity: 0.4 }
                    }}
                >
                    {T('or fill in your details')}
                </Divider>
                <Box
                    component="form"
                    display="flex"
                    flexDirection="column"
                    gap={1.25}
                    onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                        onSubmit();
                    }}
                >
                    <Box display="flex" gap={1.25} flexDirection={{ xs: 'column', sm: 'row' }}>
                        <Box flex={1}>
                            <TextField
                                label={T('First Name')}
                                required
                                error={!!errors.firstName}
                                helperText={errors.firstName}
                                labelSx={{ color: 'inherit' }}
                                disabled={submitting}
                                {...getFieldProps('firstName')}
                            />
                        </Box>
                        <Box flex={1}>
                            <TextField
                                label={T('Last Name')}
                                required
                                error={!!errors.lastName}
                                helperText={errors.lastName}
                                labelSx={{ color: 'inherit' }}
                                disabled={submitting}
                                {...getFieldProps('lastName')}
                            />
                        </Box>
                    </Box>
                    <TextField
                        label={T('Brokerage/Company Name')}
                        required
                        error={!!errors.companyName}
                        helperText={errors.companyName}
                        labelSx={{ color: 'inherit' }}
                        disabled={submitting}
                        {...getFieldProps('companyName')}
                    />
                    <TextField
                        type="email"
                        label={T('Email Address')}
                        required
                        error={!!errors.email}
                        helperText={errors.email}
                        labelSx={{ color: 'inherit' }}
                        disabled={submitting}
                        {...getFieldProps('email')}
                    />
                    <Box display="flex" gap={1.25} flexDirection={{ xs: 'column', sm: 'row' }}>
                        <Box flex={1}>
                            <TextField
                                label={T('Phone Number')}
                                required
                                error={!!errors.phone}
                                helperText={errors.phone}
                                labelSx={{ color: 'inherit' }}
                                disabled={submitting}
                                {...getFieldProps('phone')}
                            />
                        </Box>
                        <Box flex={1}>
                            <TextField
                                label={T('Referral Code (optional)')}
                                error={!!errors.referralCode}
                                helperText={errors.referralCode}
                                labelSx={{ color: 'inherit' }}
                                disabled={submitting}
                                {...getFieldProps('referralCode')}
                                // UX-797 / API-507: digits only, capped at the code length, the same
                                // way client-add.tsx filters it. The schema rejects anything else;
                                // this stops it being typed in the first place.
                                onChange={e =>
                                    setFieldValue(
                                        'referralCode',
                                        e.target.value.replace(/\D/g, '').slice(0, REFERRAL_CODE_LENGTH)
                                    )
                                }
                                inputProps={{ inputMode: 'numeric', maxLength: REFERRAL_CODE_LENGTH }}
                            />
                        </Box>
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!canSubmit || submitting}
                        sx={authSubmitButtonSx}
                    >
                        {submitting ? (
                            <CircularProgress size={22} sx={{ color: 'inherit' }} />
                        ) : (
                            T('Create Account')
                        )}
                    </Button>
                    <Box textAlign="center">
                        <RouterLink to={HUB_LOGIN_PATH} style={{ color: 'inherit', fontSize: 14 }}>
                            {T('Cancel')}
                        </RouterLink>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function RegistrationUnavailable() {
    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 900, fontSize: 36, lineHeight: 1 }}>
                    {T('Registration isn’t available here.')}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1.5 }}>
                    {T(
                        'Accounts can’t be created from this site. If you were sent a sign-up link, please use that link, or contact your photographer.'
                    )}
                </Typography>
            </Box>
            <Button component={RouterLink} to={HUB_LOGIN_PATH} variant="contained" fullWidth sx={{ py: 1 }}>
                {T('Back to Sign in')}
            </Button>
        </Box>
    );
}

function RegistrationConfirmation({ email }: { email: string }) {
    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 900, fontSize: 36, lineHeight: 1 }}>
                    {T('One quick thing')}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1.5 }}>
                    {T('Your account is almost ready! We’ve sent a confirmation email to')}{' '}
                    <MuiLink href={`mailto:${email}`} sx={{ color: 'inherit' }}>
                        {email}
                    </MuiLink>{' '}
                    {T(
                        'with a link to set your password. It should arrive within the next few minutes.'
                    )}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1.5 }}>
                    {T(
                        'If you don’t see it, check your spam or junk folder — it may have landed there.'
                    )}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'center',
                    p: 1.5,
                    border: '0.5px solid rgba(255,255,255,0.6)',
                    borderRadius: 1,
                    bgcolor: 'rgba(255,255,255,0.2)'
                }}
            >
                <HugeiconsIcon icon={Clock01IconStroke} strokeWidth={1.5} />
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 700 }}>
                    {T(
                        'The link will expire in 48 hours. Follow the link in the email to set your password.'
                    )}
                </Typography>
            </Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 700, fontSize: 12 }}>
                    {T('WHY WE ASK')}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 12, mt: 0.75 }}>
                    {T(
                        'A confirmed email means we can reach you when it counts, and it makes sure the person creating this account is actually you.'
                    )}
                </Typography>
            </Box>
            <Button
                component={RouterLink}
                to={HUB_LOGIN_PATH}
                variant="contained"
                fullWidth
                sx={{ py: 1 }}
            >
                {T('Back to Sign in')}
            </Button>
        </Box>
    );
}
